import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'motion/react';
import { RiGithubFill, RiGoogleFill, RiEyeLine, RiEyeOffLine, RiDatabase2Line } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { authClient } from '@/lib/auth-client';
import { toast } from 'sonner';

const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

export function SignupPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      const result = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: data.name,
      });

      if (result.error) {
        const errorMessage = result.error.message || 'Failed to sign up';

        // If email already exists, redirect to verification page
        if (errorMessage.toLowerCase().includes('already exists') ||
            errorMessage.toLowerCase().includes('email is taken')) {
          toast.error('Email already registered. Redirecting to verification...');
          setTimeout(() => {
            navigate({ to: '/verify-email', search: { email: data.email } });
          }, 1500);
          return;
        }

        toast.error(errorMessage);
        return;
      }

      // Success - user created and verification email sent
      toast.success('Account created! Please check your email to verify your account.');
      navigate({ to: '/verify-email', search: { email: data.email } });
    } catch (error) {
      toast.error('An unexpected error occurred');
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/dashboard`,
      });
    } catch (error) {
      toast.error('Failed to sign up with Google');
      console.error('Google sign up error:', error);
    }
  };

  const handleGithubSignUp = async () => {
    try {
      await authClient.signIn.social({
        provider: 'github',
        callbackURL: `${window.location.origin}/dashboard`,
      });
    } catch (error) {
      toast.error('Failed to sign up with GitHub');
      console.error('GitHub sign up error:', error);
    }
  };

  const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const logoSrc = currentTheme === 'dark' ? '/moniva-light.svg' : '/moniva-dark.svg';

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-background via-background to-muted/10">
      {/* Left Side - Form */}
      <div className="flex items-center justify-center flex-1 p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-full max-w-md space-y-8"
        >
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt="Moniva" className="w-10 h-10" />
            {/* <div className="flex items-center justify-center size-12 rounded-xl bg-primary/10"> */}
              {/* <RiDatabase2Line className="size-6 text-primary" /> */}
            {/* </div> */}
            <div>
              <h1 className="text-2xl font-bold">Moniva</h1>
              <p className="text-sm text-muted-foreground">Database Management</p>
            </div>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Create an account</h2>
            <p className="text-muted-foreground">
              Get started with Moniva today
            </p>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={handleGoogleSignUp}
              disabled={isLoading}
              className="w-full"
            >
              <RiGoogleFill className="size-5" />
            </Button>
            <Button
              variant="outline"
              onClick={handleGithubSignUp}
              disabled={isLoading}
              className="w-full"
            >
              <RiGithubFill className="size-5" />
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 bg-background text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        autoComplete="name"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a strong password"
                          autoComplete="new-password"
                          disabled={isLoading}
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <RiEyeOffLine className="size-4" />
                          ) : (
                            <RiEyeLine className="size-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          autoComplete="new-password"
                          disabled={isLoading}
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <RiEyeOffLine className="size-4" />
                          ) : (
                            <RiEyeLine className="size-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                {isLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 rounded-full border-primary-foreground border-t-transparent"
                  />
                ) : (
                  'Create account'
                )}
              </Button>
            </form>
          </Form>

          {/* Footer */}
          <p className="text-sm text-center text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={() => navigate({ to: '/login' })}
              className="font-medium text-primary hover:underline"
            >
              Sign in
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right Side - Image/Illustration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        className="items-center justify-center flex-1 hidden p-12 lg:flex bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="max-w-lg space-y-6 text-center relative z-10">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            src="/nodata.svg"
            alt="Database Management"
            className="w-full h-auto opacity-90"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="space-y-3"
          >
            <h3 className="text-2xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Start Managing Databases Today
            </h3>
            <p className="text-base text-muted-foreground leading-relaxed">
              Join thousands of developers who trust Moniva for their database management needs.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
